import React from 'react';
import { ArrowLeft, Copy, Check, Building2, Globe, MessageCircle, Phone, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import '../PaymentMethods/PaymentMethods.css';

const PaymentMethods: React.FC = () => {
  const navigate = useNavigate();
  const [copiedField, setCopiedField] = React.useState<string | null>(null);

  // Get order details from localStorage
  const orderReference = localStorage.getItem('currentOrderReference') || 'HC-ORDER-' + Date.now();
  const orderPlan = localStorage.getItem('currentOrderPlan') || 'monthly';
  const orderAmount = localStorage.getItem('currentOrderAmount') || '4.99';

  const copyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const bankDetails = {
    name: 'LOTUS INTERNATIONAL CARE LTD',
    accountType: 'Business',
    accountNumber: '48759165',
    sortCode: '23-05-80',
    swift: 'MYMBGB2L',
    iban: 'GB19MYMB 230580 48759165'
  };

  const whatsappNumber = '+447737024736';

  return (
    <div className="payment-methods-page">
      <div className="payment-methods-container">
        {/* Header */}
        <div className="page-header">
          <button className="back-button" onClick={() => navigate(-1)}>
            <ArrowLeft size={20} />
            Back
          </button>
          <h1 className="page-title">Secure Bank Transfer</h1>
          <p className="page-subtitle">Complete your payment via bank transfer</p>
        </div>

        {/* Order Summary */}
        <div className="order-summary">
          <h3>Order Summary</h3>
          <div className="summary-details">
            <div className="summary-item">
              <span className="label">Plan:</span>
              <span className="value">{orderPlan.charAt(0).toUpperCase() + orderPlan.slice(1)} Subscription</span>
            </div>
            <div className="summary-item">
              <span className="label">Amount:</span>
              <span className="value">£{orderAmount}</span>
            </div>
            <div className="summary-item highlight">
              <span className="label">Order Reference:</span>
              <div className="reference-value">
                <span className="value">{orderReference}</span>
                <button 
                  className={`copy-btn ${copiedField === 'orderRef' ? 'copied' : ''}`}
                  onClick={() => copyToClipboard(orderReference, 'orderRef')}
                >
                  {copiedField === 'orderRef' ? <Check size={16} /> : <Copy size={16} />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="disclaimer">
          <div className="disclaimer-icon">
            <AlertTriangle size={24} />
          </div>
          <div className="disclaimer-content">
            <h3>Important Payment Instructions</h3>
            <ul>
              <li><strong>Include your order reference number ({orderReference}) when making the bank transfer</strong></li>
              <li>Bank transfers may take 1-3 business days to process</li>
              <li>For international transfers, additional fees may apply from your bank</li>
              <li><strong>Send proof of payment through WhatsApp after completing the transfer</strong></li>
            </ul>
          </div>
        </div>

        {/* Payment Options */}
        <div className="payment-options">
          {/* Bank Transfer Section */}
          <div className="payment-option featured">
            <div className="option-header">
              <div className="option-icon bank">
                <Building2 size={24} />
              </div>
              <div className="option-info">
                <h3>Bank Transfer Details</h3>
                <p>Transfer funds directly to our business account</p>
              </div>
            </div>

            <div className="bank-details">
              {/* International Details */}
              <div className="details-section international">
                <h4>
                  <Globe size={16} />
                  For International Payments
                </h4>
                
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Account Name</label>
                    <div className="detail-value">
                      <span>{bankDetails.name}</span>
                      <button 
                        className={`copy-btn ${copiedField === 'name' ? 'copied' : ''}`}
                        onClick={() => copyToClipboard(bankDetails.name, 'name')}
                      >
                        {copiedField === 'name' ? <Check size={16} /> : <Copy size={16} />}
                      </button>
                    </div>
                  </div>

                  <div className="detail-item">
                    <label>Account Type</label>
                    <div className="detail-value">
                      <span>{bankDetails.accountType}</span>
                      <button 
                        className={`copy-btn ${copiedField === 'type' ? 'copied' : ''}`}
                        onClick={() => copyToClipboard(bankDetails.accountType, 'type')}
                      >
                        {copiedField === 'type' ? <Check size={16} /> : <Copy size={16} />}
                      </button>
                    </div>
                  </div>

                  <div className="detail-item">
                    <label>Account Number</label>
                    <div className="detail-value">
                      <span className="monospace">{bankDetails.accountNumber}</span>
                      <button 
                        className={`copy-btn ${copiedField === 'account' ? 'copied' : ''}`}
                        onClick={() => copyToClipboard(bankDetails.accountNumber, 'account')}
                      >
                        {copiedField === 'account' ? <Check size={16} /> : <Copy size={16} />}
                      </button>
                    </div>
                  </div>

                  <div className="detail-item">
                    <label>Sort Code</label>
                    <div className="detail-value">
                      <span className="monospace">{bankDetails.sortCode}</span>
                      <button 
                        className={`copy-btn ${copiedField === 'sort' ? 'copied' : ''}`}
                        onClick={() => copyToClipboard(bankDetails.sortCode, 'sort')}
                      >
                        {copiedField === 'sort' ? <Check size={16} /> : <Copy size={16} />}
                      </button>
                    </div>
                  </div>

                  <div className="detail-item">
                    <label>SWIFT/BIC</label>
                    <div className="detail-value">
                      <span className="monospace">{bankDetails.swift}</span>
                      <button 
                        className={`copy-btn ${copiedField === 'swift' ? 'copied' : ''}`}
                        onClick={() => copyToClipboard(bankDetails.swift, 'swift')}
                      >
                        {copiedField === 'swift' ? <Check size={16} /> : <Copy size={16} />}
                      </button>
                    </div>
                  </div>

                  <div className="detail-item full-width">
                    <label>IBAN</label>
                    <div className="detail-value">
                      <span className="monospace">{bankDetails.iban}</span>
                      <button 
                        className={`copy-btn ${copiedField === 'iban' ? 'copied' : ''}`}
                        onClick={() => copyToClipboard(bankDetails.iban, 'iban')}
                      >
                        {copiedField === 'iban' ? <Check size={16} /> : <Copy size={16} />}
                      </button>
                    </div>
                  </div>

                  <div className="detail-item full-width">
                    <label>Payment Reference (REQUIRED)</label>
                    <div className="detail-value reference-highlight">
                      <span className="monospace">{orderReference}</span>
                      <button 
                        className={`copy-btn ${copiedField === 'reference' ? 'copied' : ''}`}
                        onClick={() => copyToClipboard(orderReference, 'reference')}
                      >
                        {copiedField === 'reference' ? <Check size={16} /> : <Copy size={16} />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* WhatsApp Contact */}
          <div className="payment-option">
            <div className="option-header">
              <div className="option-icon whatsapp">
                <MessageCircle size={24} />
              </div>
              <div className="option-info">
                <h3>Send Proof of Payment</h3>
                <p>WhatsApp us your payment confirmation</p>
              </div>
            </div>
            <div className="whatsapp-section">
              <p>After completing your bank transfer, please send proof of payment through WhatsApp for faster processing. Include your order reference <strong>{orderReference}</strong> in your message:</p>
              <div className="whatsapp-contact">
                <div className="contact-detail">
                  <Phone size={16} />
                  <span className="monospace">{whatsappNumber}</span>
                  <button 
                    className={`copy-btn ${copiedField === 'whatsapp' ? 'copied' : ''}`}
                    onClick={() => copyToClipboard(whatsappNumber, 'whatsapp')}
                  >
                    {copiedField === 'whatsapp' ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
                <a 
                  href={`https://wa.me/${whatsappNumber.replace('+', '')}?text=Hi, I have completed a bank transfer for HomeCookt subscription. Order Reference: ${orderReference}. Please find the payment proof attached.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whatsapp-btn"
                >
                  <MessageCircle size={16} />
                  Send Payment Proof
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethods;